import moment from 'moment-timezone';
import React, { FC, useState } from 'react';
import { FCOFields, FeatureViewFCO, FeatureViewFCOFields, OnlineStoreType } from '../../../core/types/fcoTypes';
import {
  BATCH_FEATURE_VIEW_MONITORING_TAB,
  COMMON_DYNAMODB_FEATURE_VIEW_STORE_TYPE,
  COMMON_REDIS_FEATURE_VIEW_STORE_TYPE,
  CONTINUES_FEATURE_VIEW_MONITORING_TAB,
  PUSH_FEATURE_VIEW_MONITORING_TAB,
  STREAM_FEATURE_VIEW_MONITORING_TAB,
} from '../../../feature/graph-configuration';
import { useUserSettings } from '../../context/UserSettingsContext';
import {
  MonitoringDateRange,
  calculateStep,
  calculateUnit,
  getDefaultMonitoringDateRange,
} from '../../monitoring/DateRange-Dropdown-util';
import MonitoringGraphs from '../monitoring/MonitoringGraphs';

// @tecton
import { Empty, FlexGroup, FlexItem, Loading } from '@lithos';
import { useWorkspaceList } from '../../../shared/query';
import TimeSelector from '../../@tecton/TimeSelector';

interface FeatureViewDetailMonitoringProps {
  featureview: FeatureViewFCO;
}

const FeatureViewMonitoringDevelopmentWorkspaceWrapper: FC<{
  featureview: FeatureViewFCO;
  children: React.ReactNode;
}> = ({ featureview, children }) => {
  const { workspace } = useUserSettings();

  const { data, isLoading: workspacesLoading } = useWorkspaceList();
  if (workspacesLoading) {
    return <Loading />;
  }

  const isDevelopmentWorkspace = data.workspaces.find((findWorkspace: any) => findWorkspace.name === workspace);

  if (!isDevelopmentWorkspace || !isDevelopmentWorkspace.capabilities.materializable === true) {
    return (
      <Empty
        useCase="empty"
        description={
          <>{workspace} is a development workspace. Monitoring is not available in development workspaces.</>
        }
        title={<>Monitoring Not Available</>}
        placement="embedded"
        actions={[]}
      />
    );
  }

  if (!featureview[FeatureViewFCOFields.IS_ONLINE_MATERIALIZATION_ENABLED]) {
    return (
      <Empty
        useCase="empty"
        description={<>Enable online materialization for "{featureview.name}" to view online Monitoring.</>}
        title={<>Online Materialization is not enabled</>}
        placement="embedded"
        actions={[]}
      />
    );
  }

  return <>{children}</>;
};

const FeatureViewDetailMonitoring: FC<FeatureViewDetailMonitoringProps> = ({ featureview }) => {
  const { workspace } = useUserSettings();
  const defaultMonitoringDateRange: MonitoringDateRange = getDefaultMonitoringDateRange();
  const [monitoringDateRange, setMonitoringDateRange] = useState<MonitoringDateRange>(defaultMonitoringDateRange);

  const batchOnly = !featureview[FeatureViewFCOFields.IS_STREAM];
  const isPush = featureview[FeatureViewFCOFields.IS_PUSH];
  const continuousPackage = featureview[FeatureViewFCOFields.IS_CONTINUOUS];

  let graphData = STREAM_FEATURE_VIEW_MONITORING_TAB; // Default

  if (batchOnly) {
    graphData = BATCH_FEATURE_VIEW_MONITORING_TAB;
  } else if (isPush) {
    graphData = PUSH_FEATURE_VIEW_MONITORING_TAB;
  } else if (continuousPackage) {
    graphData = CONTINUES_FEATURE_VIEW_MONITORING_TAB;
  }

  return (
    <FeatureViewMonitoringDevelopmentWorkspaceWrapper featureview={featureview}>
      <FlexGroup gapSize="4" direction="column" padding="0" css={{ overflow: 'scroll' }}>
        <FlexItem grow="0" shrink="0">
          <TimeSelector
            onSelection={(start, end) => {
              const step = calculateStep(moment(start), moment(end));
              const unit = calculateUnit(moment(start), moment(end));
              setMonitoringDateRange({
                startTime: () => start.valueOf(),
                endTime: () => end.valueOf(),
                requestId: `${start.valueOf()}${end.valueOf()}`,
                step,
                unit,
              });
            }}
          />
        </FlexItem>
        <FlexItem grow="1" shrink="0">
          <MonitoringGraphs
            monitoringDateRange={monitoringDateRange}
            graphsData={graphData}
            customGraphRecord={{}}
            workspace={workspace ?? ''}
            fcoId={featureview[FCOFields.ID] ?? ''}
          />
        </FlexItem>

        {featureview[FeatureViewFCOFields.ONLINE_STORE_TYPE] === OnlineStoreType.DYNAMO && (
          <FlexItem grow="1" shrink="0">
            <MonitoringGraphs
              monitoringDateRange={monitoringDateRange}
              graphsData={COMMON_DYNAMODB_FEATURE_VIEW_STORE_TYPE}
              customGraphRecord={{}}
              workspace={workspace ?? ''}
              fcoId={featureview[FCOFields.ID]}
            />
          </FlexItem>
        )}

        {featureview[FeatureViewFCOFields.ONLINE_STORE_TYPE] === OnlineStoreType.REDIS && (
          <FlexItem>
            <MonitoringGraphs
              monitoringDateRange={monitoringDateRange}
              graphsData={COMMON_REDIS_FEATURE_VIEW_STORE_TYPE}
              customGraphRecord={{}}
              workspace={workspace ?? ''}
              fcoId={featureview[FCOFields.ID]}
            />
          </FlexItem>
        )}
      </FlexGroup>
    </FeatureViewMonitoringDevelopmentWorkspaceWrapper>
  );
};

export default FeatureViewDetailMonitoring;
